import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import type { ReactNode } from "react";
import { PAGE_CONTAINER } from "./site";
// Groepenkastfoto voor de 3-fasebanner; vervang deze import voor een ander beeld.
import foto3FaseSrc from "@/assets/groepenkast-aeg-transparant.png";
// Officiële FlameGuard-productfoto; vervang deze import voor een nieuw productbeeld.
import flameGuardProductSrc from "@/assets/flameguard-transparant.png";
import badgeBesteKoop from "@/assets/badge-beste-koop.png.asset.json";

export const FLAMEGUARD_FOTO = flameGuardProductSrc;

const kaart = "grid h-full overflow-hidden rounded-[20px] border border-[#E3E6EB] bg-white text-[#14213D] shadow-[0_1px_2px_rgba(20,33,61,0.06)] min-[1024px]:grid-cols-[minmax(0,1fr)_30%]";
const knop = "inline-flex h-11 items-center gap-1.5 whitespace-nowrap rounded-full px-[18px] text-[14px] font-semibold transition-colors";
const primair = `${knop} bg-[#14213D] text-white hover:bg-[#24345A]`;
const secundair = `${knop} border-[1.5px] border-[#14213D] bg-white text-[#14213D] hover:bg-[#F4F5F7]`;

function Banner({ titel, tekst, vinkjes, knoppen, foto, alt, fit = "cover", badge }: { titel: string; tekst: string; vinkjes: ReactNode[]; knoppen: ReactNode; foto: string; alt: string; fit?: "cover" | "contain"; badge?: { src: string; alt: string } }) {
  // Productfoto op lichte studioachtergrond: het paneel krijgt een grijzer verloop
  // dan de kaart zelf, zodat de foto er dieper op staat en naadloos overloopt.
  const paneel = fit === "contain"
    ? " h-[150px] lg:h-full bg-gradient-to-b from-[#F5F7FA] to-[#DFE4EC]"
    : " h-[200px]";
  return (
    <div className="relative">
      {badge && (
        <img src={badge.src} alt={badge.alt} width={180} height={234} loading="lazy" className="absolute -top-3 right-5 z-10 w-[64px] md:w-[76px] lg:right-[calc(30%_-_14px)] min-[1400px]:right-[calc(30%_-_18px)]! min-[1400px]:top-1 min-[1400px]:w-[84px]" />
      )}
      <div className={kaart}>
      <div className={`relative order-first w-full lg:order-last lg:self-stretch${paneel}`}>
        <img src={foto} alt={alt} width={1024} height={1024} loading="lazy" className={`absolute inset-0 size-full ${fit === "contain" ? "object-contain p-3 lg:px-6 lg:py-14" : "object-cover"}`} />
        <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 hidden w-6 bg-gradient-to-r from-white to-transparent hidden! min-[1400px]:w-8 lg:block" />
      </div>
      <div className="flex min-w-0 flex-col p-6 md:p-[28px]">
        <h2 className="m-0 text-[22px] font-extrabold leading-tight tracking-tight md:text-[24px] lg:max-w-[270px] xl:max-w-[330px]">{titel}</h2>
        <p className="mt-3 text-[15px] leading-[1.55] text-[#4A5468] md:text-base lg:max-w-[560px]">{tekst}</p>
        <ul className="mt-4 flex list-none flex-col gap-2 p-0">
          {vinkjes.map((v, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[15px] text-[#14213D]">
              <Check className="mt-0.5 size-4 shrink-0 text-[#1F7A4D]" strokeWidth={2} aria-hidden="true" />
              <span>{v}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex flex-wrap gap-[10px]">{knoppen}</div>
      </div>
      </div>
    </div>
  );
}

export function InfoBanners() {
  return (
    <section className={`${PAGE_CONTAINER} pt-10 pb-[30px]`} aria-label="Informatie over 3-fase aansluiting en brandveiligheid">
      <div className="grid gap-4 md:gap-6">
        <Banner
          titel="Over op 3-fase?"
          tekst="Een 3-fase aansluiting vraag je zelf aan bij je netbeheerder via MijnAansluiting.nl. Daarna bouwt een elektricien je groepenkast om."
          foto={foto3FaseSrc}
          fit="contain"
          alt="Groepenkast met installatieautomaten en een hoofdschakelaar"
          vinkjes={[
            "Zelf aanvragen via MijnAansluiting.nl",
            "Netbeheerder past aansluiting en meter aan",
            <Link key="g" to="/elektricien/$dienst" params={{ dienst: "groepenkast" }} className="text-[#14213D] underline-offset-4 hover:underline">Elektricien bouwt je groepenkast om</Link>,
          ]}
          knoppen={<>
            <a href="https://www.mijnaansluiting.nl/home" target="_blank" rel="noopener" className={primair}>Verzwaring aanvragen <ArrowUpRight className="size-4" aria-hidden="true" /></a>
            <Link to="/netbeheerders" className={secundair}>Wie is mijn netbeheerder?</Link>
          </>}
        />
        <Banner
          titel="Extra brandveiligheid in je groepenkast"
          tekst="FlameGuard klikt op de DIN-rail in je groepenkast en blust automatisch als de temperatuur boven ± 170 °C komt."
          foto={FLAMEGUARD_FOTO}
          fit="contain"
          alt="FlameGuard-brandveiligheidsmodule met vlamicoon en de tekst brandpreventie"
          badge={{ src: badgeBesteKoop.url, alt: "Elvion Group Beste Koop" }}
          vinkjes={["Zonder stroom of onderhoud", "Gaat 10 jaar mee", "Neemt 1 module in"]}
          knoppen={<>
            <a href="https://flameguard-nederland.com" target="_blank" rel="noopener" className={primair}>Bekijk FlameGuard <ArrowUpRight className="size-4" aria-hidden="true" /></a>
            <Link to="/elektricien/$dienst" params={{ dienst: "groepenkast" }} className={secundair}>Groepenkast laten checken</Link>
          </>}
        />
      </div>
    </section>
  );
}
